import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AuthorizedLayout } from "../Layouts/AuthorizedLayout";
import BoxHeader from "../../Components/Box/BoxHeader";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Typography,
  Box,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { AntTab, AntTabs } from "../../Components/Tab/AntTab";
import { GetApiMethod, DeleteApiMethod } from "../../Utils/ApiService";
import { showLoader } from "../../Store/loaderSlice";
import MenuGroup from "../../Components/DropDown/MenuGroup";
import "yet-another-react-lightbox/styles.css";
import Lightbox from "yet-another-react-lightbox";

const PostDetail = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [tab, setTab] = useState(0);
  const [index, setIndex] = useState(-1);

  const imageSlides = post
    ? post.postPostMediaDetails
        .filter((m) => m.mediaType && m.mediaType.startsWith("image"))
        .map((m) => ({
          src: m.mediaUrl,
        }))
    : [];

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const getPost = useCallback(async () => {
    if (!id) return;
    try {
      dispatch(showLoader({ showLoader: true }));

      const [postRes, likesRes, commentsRes] = await Promise.all([
        GetApiMethod(`posts/${id}`),
        GetApiMethod(`likes/${id}`),
        GetApiMethod(`comments/all/${id}`),
      ]);

      const fetchedPost = postRes.data.data;

      const images =
        fetchedPost.postPostMediaDetails?.filter(
          (m) => m.mediaType && m.mediaType.startsWith("image")
        ) || [];

      const videos =
        fetchedPost.postPostMediaDetails?.filter(
          (m) => m.mediaType && m.mediaType.startsWith("video")
        ) || [];

      setPost({
        ...fetchedPost,
        likes: likesRes.data.data.rows || [],
        likesCount: likesRes.data.data.count || 0,
        comments: commentsRes.data.data.rows || [],
        commentsCount: commentsRes.data.data.count || 0,
        media: fetchedPost.postPostMediaDetails || [],
        mediaCount: images.length + videos.length,
        imageCount: images.length,
        videoCount: videos.length,
      });
    } catch (err) {
      console.error(err);
    } finally {
      dispatch(showLoader({ showLoader: false }));
    }
  }, [dispatch, id]);

  useEffect(() => {
    getPost();
  }, [getPost]);

  // 🔹 Delete Like
  const handleDeleteLike = async (likeId) => {
    try {
      await DeleteApiMethod(`like/${likeId}`);
      console.log("Like deleted successfully");
      return;
    } catch (err) {
      console.error("Error deleting like:", err);
    }
  };

  if (!post) return null;

  const getLocationName = (post) => {
    if (post.locationName) {
      return post.locationName;
    } else if (post.postLocationDetails?.name) {
      return post.postLocationDetails.name;
    } else {
      return "-";
    }
  };

  return (
    <AuthorizedLayout>
      <BoxHeader dashboard_name="Post Management">
        <AntTabs value={tab} onChange={(_, newTab) => setTab(newTab)}>
          <AntTab label="Post Details" />
          <AntTab label={`Likes (${post.likes?.length || 0})`} />
          <AntTab label={`Comments (${post.commentsCount || 0})`} />
          <AntTab label={`Media (${post.mediaCount || 0})`} />
        </AntTabs>
      </BoxHeader>

      <Button
        onClick={() => navigate("/posts")}
        sx={{
          my: 2,
          color: "white",
          backgroundColor: "#09BBE9",
          "&:hover": { backgroundColor: "#09BBE9" },
        }}
        startIcon={<ArrowBack />}
      >
        Back
      </Button>

      {tab === 0 && (
        <TableContainer className="mt-2">
          <Table>
            <TableBody>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>
                  {post.postUserDetails
                    ? `${post.postUserDetails.firstName} ${post.postUserDetails.lastName}`
                    : "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Content</TableCell>
                <TableCell>{post.content}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>{post.postMode}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Tags</TableCell>
                <TableCell>{post.postTagDetails?.name || "-"}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Tagged Users</TableCell>
                {post.postPostTagUsers.length > 0 ? (
                  post.postPostTagUsers.map((eachTaggedUser) => (
                    <TableCell>
                      {eachTaggedUser.postTagUsers
                        ? `${eachTaggedUser.postTagUsers.firstName} ${eachTaggedUser.postTagUsers.lastName}`
                        : "-"}
                    </TableCell>
                  ))
                ) : (
                  <TableCell>-</TableCell>
                )}
              </TableRow>
              <TableRow>
                <TableCell>Location</TableCell>
                <TableCell>{getLocationName(post)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Created At</TableCell>
                <TableCell>
                  {post.createdAt
                    ? new Date(post.createdAt).toLocaleString("en-AU", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                      })
                    : "-"}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tab === 1 && (
        <TableContainer className="mt-2">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Liked At</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {post.likes && post.likes.length > 0 ? (
                post.likes.map((like) => (
                  <TableRow key={like.id}>
                    <TableCell>
                      {like.likeUserDetails
                        ? `${like.likeUserDetails.firstName} ${like.likeUserDetails.lastName}`
                        : "Unknown"}
                    </TableCell>
                    <TableCell>
                      {new Date(like.createdAt).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <MenuGroup
                        id={like.id}
                        view={false}
                        edit={false}
                        delete={true}
                        clickDelete={() => handleDeleteLike(like.id)}
                      />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    No likes
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* --- Tab 2: Comments --- */}
      {tab === 2 && (
        <TableContainer className="mt-2">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Comment</TableCell>
                <TableCell>Commented At</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {post.comments.length > 0 ? (
                post.comments.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>
                      {c.commentUserDetails
                        ? `${c.commentUserDetails.firstName} ${c.commentUserDetails.lastName}`
                        : "Unknown"}
                    </TableCell>
                    <TableCell>{c.comment}</TableCell>
                    <TableCell>
                      {new Date(c.createdAt).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    No comments
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tab === 3 && (
        <Box sx={{ mt: 2 }}>
          {post.postPostMediaDetails && post.postPostMediaDetails.length > 0 ? (
            <>
              {/* Images Section */}
              <Typography variant="h6" sx={{ mb: 1 }}>
                Images
              </Typography>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
                {imageSlides.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No images available
                  </Typography>
                ) : (
                  <>
                    {imageSlides.map((m, index) => {
                      const fileUrl = m.src;
                      return (
                        <button
                          key={index}
                          style={{
                            backgroundColor: "transparent",
                            border: "none",
                            padding: 0,
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            setIndex(index);
                          }}
                        >
                          <img
                            src={fileUrl}
                            alt={m.mediaType}
                            style={{
                              width: 200,
                              height: 140,
                              objectFit: "cover",
                              borderRadius: 8,
                              backgroundColor: "#000",
                            }}
                          />
                        </button>
                      );
                    })}
                  </>
                )}

                <Lightbox
                  index={index}
                  slides={imageSlides}
                  open={index >= 0}
                  close={() => setIndex(-1)}
                  carousel={{
                    finite: true, // ⬅️ disables Next on last image & Prev on first
                  }}
                />
              </Box>

              {/* Videos Section */}
              <Typography variant="h6" sx={{ mb: 1 }}>
                Videos
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                {post.postPostMediaDetails
                  .filter((m) => m.mediaType && m.mediaType.startsWith("video"))
                  .map((m) => {
                    const baseUrl = (
                      process.env.REACT_APP_API_URL || ""
                    ).replace(/\/v1$/, "");
                    const fileUrl = `${m.mediaUrl}`;
                    return (
                      <video
                        key={m.id}
                        src={fileUrl}
                        controls
                        style={{
                          width: 200,
                          height: 140,
                          objectFit: "cover",
                          borderRadius: 8,
                          backgroundColor: "#000",
                        }}
                      />
                    );
                  })}
                {post.postPostMediaDetails.filter((m) =>
                  m.mediaType.startsWith("video")
                ).length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No videos available
                  </Typography>
                )}
              </Box>
            </>
          ) : (
            <Typography>No media available</Typography>
          )}
        </Box>
      )}
    </AuthorizedLayout>
  );
};

export default PostDetail;
